import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Send, AlertCircle } from "lucide-react";
import { track } from "../lib/analytics";

/**
 * BIaaSIntakeForm — Lead capture form. POSTs to /api/hubspot-lead which
 * persists the lead locally and (if configured) mirrors it to HubSpot.
 */
const GOLD = "#C9A84C";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SECTORS = ["Restaurant", "Real Estate", "Contractor", "Music/Creative", "TechOps", "Other"];
const TIERS = [
    "Platinum · Foundation",
    "Rose Gold · Command",
    "Diamond · Sovereign",
    "Not sure yet",
];

const fieldStyle = {
    background: "#0A0A0A",
    border: "1px solid rgba(201,168,76,0.18)",
    color: "#EDEDED",
    padding: "12px 14px",
    borderRadius: "8px",
    width: "100%",
    fontSize: "14px",
    fontFamily: "'Inter', 'Montserrat', sans-serif",
    outline: "none",
    transition: "border-color 0.2s ease",
};
const labelStyle = {
    display: "block",
    marginBottom: 8,
    fontSize: "11px",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#A8A8A8",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
};

function Field({ label, required, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span style={labelStyle}>
                {label}
                {required && <span style={{ color: GOLD, marginLeft: 4 }}>*</span>}
            </span>
            {children}
        </label>
    );
}

export default function BIaaSIntakeForm() {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        business_sector: "",
        primary_challenge: "",
        preferred_tier: "",
    });
    const [status, setStatus] = useState("idle"); // idle | busy | ok | err
    const [errMsg, setErrMsg] = useState("");
    const [okMsg, setOkMsg] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setErrMsg("");
        if (!form.full_name.trim() || !form.email.trim() || !form.business_sector) {
            setErrMsg("Please complete the required fields.");
            setStatus("err");
            return;
        }
        setStatus("busy");
        try {
            const { data } = await axios.post(`${API_BASE}/api/hubspot-lead`, form, {
                headers: { "Content-Type": "application/json" },
            });
            track("biaas_form_submit", {
                sector: form.business_sector,
                tier: form.preferred_tier || "unspecified",
                lead_id: data?.lead_id,
            });
            setOkMsg(
                data?.message ||
                    "Signal received. Our team will reach out within 24 hours to schedule your strategy call."
            );
            setStatus("ok");
            setForm({
                full_name: "",
                email: "",
                business_sector: "",
                primary_challenge: "",
                preferred_tier: "",
            });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "We couldn't submit your signal right now. Please try again in a moment.";
            track("biaas_form_error", {
                status: err?.response?.status || 0,
                sector: form.business_sector || "unspecified",
            });
            setErrMsg(typeof detail === "string" ? detail : "Please check the highlighted fields.");
            setStatus("err");
        }
    };

    return (
        <section
            id="biaas-intake"
            data-testid="biaas-intake-section"
            className="relative py-24 md:py-32"
            style={{ background: "#050505" }}
        >
            <div className="max-w-[900px] mx-auto px-6 md:px-10">
                <div className="max-w-2xl mb-12">
                    <div
                        className="text-[10px] uppercase tracking-[0.4em] mb-4"
                        style={{ color: GOLD, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                        data-testid="biaas-overline"
                    >
                        Intake
                    </div>
                    <h2
                        className="text-4xl md:text-5xl leading-[1.1] tracking-tight"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: "#EDEDED",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Start Your Infrastructure Upgrade
                    </h2>
                    <p
                        className="mt-4 text-base md:text-lg"
                        style={{ color: "#A8A8A8", fontFamily: "'Inter', 'Montserrat', sans-serif" }}
                    >
                        Tell us about your business and we&rsquo;ll map the right solution.
                    </p>
                </div>

                {status === "ok" ? (
                    <div
                        data-testid="biaas-form-success"
                        className="p-8 rounded-[10px] flex items-start gap-4"
                        style={{
                            background: "#0F0D08",
                            border: `1px solid ${GOLD}`,
                            boxShadow: "0 0 24px rgba(201,168,76,0.15)",
                        }}
                    >
                        <CheckCircle2 size={28} style={{ color: GOLD, flexShrink: 0 }} />
                        <div>
                            <div
                                className="text-lg mb-2"
                                style={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    color: "#EDEDED",
                                }}
                            >
                                Signal received.
                            </div>
                            <p
                                className="text-sm leading-relaxed"
                                style={{ color: "#C8C8C8", fontFamily: "'Inter', sans-serif" }}
                            >
                                {okMsg}
                            </p>
                            <button
                                type="button"
                                onClick={() => setStatus("idle")}
                                data-testid="biaas-form-reset"
                                className="mt-5 text-xs uppercase tracking-[0.25em] transition-colors"
                                style={{ color: GOLD, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                            >
                                Submit another →
                            </button>
                        </div>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        data-testid="biaas-form"
                        className="space-y-5"
                        style={{
                            background: "#0A0A0A",
                            border: "1px solid rgba(201,168,76,0.14)",
                            borderRadius: "12px",
                            padding: "32px",
                        }}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="Full Name" required testid="biaas-field-name">
                                <input
                                    type="text"
                                    required
                                    value={form.full_name}
                                    onChange={update("full_name")}
                                    placeholder="Jane Sovereign"
                                    style={fieldStyle}
                                    data-testid="biaas-input-name"
                                    onFocus={(e) => (e.target.style.borderColor = GOLD)}
                                    onBlur={(e) =>
                                        (e.target.style.borderColor = "rgba(201,168,76,0.18)")
                                    }
                                />
                            </Field>
                            <Field label="Email" required testid="biaas-field-email">
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={update("email")}
                                    placeholder="jane@yourcompany.com"
                                    style={fieldStyle}
                                    data-testid="biaas-input-email"
                                    onFocus={(e) => (e.target.style.borderColor = GOLD)}
                                    onBlur={(e) =>
                                        (e.target.style.borderColor = "rgba(201,168,76,0.18)")
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Business Sector" required testid="biaas-field-sector">
                            <select
                                required
                                value={form.business_sector}
                                onChange={update("business_sector")}
                                style={fieldStyle}
                                data-testid="biaas-input-sector"
                                onFocus={(e) => (e.target.style.borderColor = GOLD)}
                                onBlur={(e) => (e.target.style.borderColor = "rgba(201,168,76,0.18)")}
                            >
                                <option value="">Select a sector</option>
                                {SECTORS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Primary Challenge" testid="biaas-field-challenge">
                            <input
                                type="text"
                                value={form.primary_challenge}
                                onChange={update("primary_challenge")}
                                placeholder="What's the biggest operational bottleneck you're facing?"
                                style={fieldStyle}
                                data-testid="biaas-input-challenge"
                                onFocus={(e) => (e.target.style.borderColor = GOLD)}
                                onBlur={(e) => (e.target.style.borderColor = "rgba(201,168,76,0.18)")}
                            />
                        </Field>

                        <Field label="Preferred Tier" testid="biaas-field-tier">
                            <select
                                value={form.preferred_tier}
                                onChange={update("preferred_tier")}
                                style={fieldStyle}
                                data-testid="biaas-input-tier"
                                onFocus={(e) => (e.target.style.borderColor = GOLD)}
                                onBlur={(e) => (e.target.style.borderColor = "rgba(201,168,76,0.18)")}
                            >
                                <option value="">Not sure yet</option>
                                {TIERS.map((t) => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        {status === "err" && errMsg && (
                            <div
                                data-testid="biaas-form-error"
                                className="flex items-start gap-2 p-3 rounded-md text-sm"
                                style={{
                                    background: "rgba(255,100,100,0.06)",
                                    border: "1px solid rgba(255,100,100,0.25)",
                                    color: "#F8B4B4",
                                }}
                            >
                                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span>{errMsg}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status === "busy"}
                            data-testid="biaas-submit"
                            className="group inline-flex items-center justify-center gap-2 w-full md:w-auto px-8 py-3.5 font-bold uppercase tracking-[0.22em] text-sm transition-all duration-300"
                            style={{
                                background: GOLD,
                                color: "#0A0A0A",
                                border: `1px solid ${GOLD}`,
                                fontFamily: "'Montserrat', sans-serif",
                                boxShadow: "0 0 22px rgba(201,168,76,0.28)",
                                opacity: status === "busy" ? 0.6 : 1,
                                cursor: status === "busy" ? "not-allowed" : "pointer",
                                borderRadius: "8px",
                            }}
                            onMouseEnter={(e) => {
                                if (status !== "busy") {
                                    e.currentTarget.style.boxShadow =
                                        "0 0 34px rgba(201,168,76,0.55)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "0 0 22px rgba(201,168,76,0.28)";
                            }}
                        >
                            <Send size={16} />
                            {status === "busy" ? "Sending..." : "Send My Signal"}
                        </button>
                    </form>
                )}
            </div>
        </section>
    );
}
